import React, { useState, useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import Badge from "@mui/material/Badge";
import { styled } from "@mui/material/styles";
import NotificationsIcon from "@mui/icons-material/Notifications";
import MessageIcon from "@mui/icons-material/Message";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import StorefrontOutlinedIcon from "@mui/icons-material/StorefrontOutlined";
import BusinessOutlinedIcon from "@mui/icons-material/BusinessOutlined";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import LogoutOutlinedIcon from "@mui/icons-material/LogoutOutlined";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import Nav from "react-bootstrap/Nav";
import dummyUser from "../../../assets/images/dummy/dummy-user.png";
import { getAsset } from "../../../helpers/helper";
import NavbarSearch from "./NavbarSearch";
import NavbarAIChat from "../NavbarAIChat";
import "./NavbarActions.css";

const resolveComponent = (comp, fallback = "div") => {
  if (!comp) return fallback;
  if (comp.default && (typeof comp.default === "function" || typeof comp.default === "object")) {
    return comp.default;
  }
  return comp;
};

const RawBadge = resolveComponent(Badge, "span");
const StyledBadge =
  typeof styled === "function" && RawBadge
    ? styled(RawBadge)(({ theme }) => ({
        "& .MuiBadge-badge": {
          right: -3,
          top: -7,
          border: `2px solid ${
            (theme &&
              theme.palette &&
              theme.palette.background &&
              theme.palette.background.paper) ||
            "#ffffff"
          }`,
          padding: "0 4px",
        },
      }))
    : RawBadge;

/**
 * Resolves the uploaded profile image URL for the user.
 * Returns null if no valid image was uploaded.
 */
const resolveUploadedImage = (userData) => {
  if (!userData) return null;

  let raw = null;
  if (Array.isArray(userData.imageUrl) && userData.imageUrl.length > 0) {
    raw = userData.imageUrl.find(
      (item) => typeof item === "string" && item.trim() !== "",
    );
  } else if (
    typeof userData.imageUrl === "string" &&
    userData.imageUrl.trim() !== ""
  ) {
    raw = userData.imageUrl;
  } else if (
    typeof userData.profileImage === "string" &&
    userData.profileImage.trim() !== ""
  ) {
    raw = userData.profileImage;
  } else if (
    typeof userData.profilePic === "string" &&
    userData.profilePic.trim() !== ""
  ) {
    raw = userData.profilePic;
  } else if (
    typeof userData.avatar === "string" &&
    userData.avatar.trim() !== ""
  ) {
    raw = userData.avatar;
  }

  if (raw) {
    return getAsset(raw);
  }
  return null;
};

const NavbarActions = ({
  className = "",
  notifications = [],
  onLogout,
  onMessages,
  onNotifications,
  unreadMsgCount = 0,
  user,
}) => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [imageFailed, setImageFailed] = useState(false);
  const [isAIChatOpen, setIsAIChatOpen] = useState(false);
  const [isAIChatMinimized, setIsAIChatMinimized] = useState(false);
  const dropdownRef = useRef(null);
  const navigate = useNavigate();

  const reduxUser = useSelector((state) => state.user?.name);
  const currentUser = user || reduxUser;

  const unreadNotificationCount = Array.isArray(notifications)
    ? notifications.filter((n) => !n.readAt).length
    : 0;

  // Determine if user is logged in
  const isLoggedIn = Boolean(
    currentUser &&
    typeof currentUser === "object" &&
    (currentUser._id || currentUser.email || currentUser.fullname),
  );

  // Reset image error state whenever the user entity or image changes
  useEffect(() => {
    setImageFailed(false);
  }, [
    currentUser?._id,
    currentUser?.imageUrl,
    currentUser?.profileImage,
    currentUser?.profilePic,
  ]);

  // Handle outside clicks and keyboard escape key to smoothly close dropdown
  useEffect(() => {
    if (!isDropdownOpen) return;

    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("touchstart", handleClickOutside);
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("touchstart", handleClickOutside);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isDropdownOpen]);

  const uploadedImage = resolveUploadedImage(currentUser);
  const avatarSrc = !imageFailed && uploadedImage ? uploadedImage : dummyUser;

  const displayName =
    currentUser?.fullname ||
    currentUser?.name ||
    currentUser?.username ||
    "My Account";

  const displayEmail = currentUser?.email || "";

  const handleNavigate = (path) => {
    setIsDropdownOpen(false);
    navigate(path);
  };

  const handleLogoutClick = () => {
    setIsDropdownOpen(false);
    if (typeof onLogout === "function") {
      onLogout();
    }
  };

  const NavComp = resolveComponent(Nav, "div");
  const NavLinkComp = Nav && Nav.Link ? resolveComponent(Nav.Link, "div") : "div";
  const BadgeComp = resolveComponent(StyledBadge, RawBadge);
  const MessageComp = resolveComponent(MessageIcon, () => null);
  const NotificationsComp = resolveComponent(NotificationsIcon, () => null);
  const PersonOutlineComp = resolveComponent(PersonOutlineIcon, () => null);
  const StorefrontComp = resolveComponent(StorefrontOutlinedIcon, () => null);
  const BusinessComp = resolveComponent(BusinessOutlinedIcon, () => null);
  const SettingsComp = resolveComponent(SettingsOutlinedIcon, () => null);
  const LogoutComp = resolveComponent(LogoutOutlinedIcon, () => null);

  return (
    <NavComp className={`navbar-actions-container gap-2 gap-md-3 navbar-mobile-actions ${className}`.trim()}>
      {/* Top Navbar Search Box with Voice Icon on the right side */}
      <NavbarSearch />

      <button
        type="button"
        className="navbar-ai-trigger"
        onClick={() => {
          setIsAIChatOpen(true);
          setIsAIChatMinimized(false);
        }}
        aria-label="Ask AI"
      >
        <AutoAwesomeIcon className="navbar-ai-trigger-icon" />
        <span>Ask AI</span>
      </button>

      {/* Notifications Link */}
      <NavLinkComp
        className="notify-class p-1 p-sm-2"
        onClick={onNotifications}
        aria-label="Notifications"
      >
        <BadgeComp
          badgeContent={
            unreadNotificationCount > 9 ? "9+" : unreadNotificationCount
          }
          color="primary"
        >
          <NotificationsComp className="icon_setting" />
        </BadgeComp>
      </NavLinkComp>
      {/* Profile Avatar & Animated Smooth Dropdown (when logged in) */}
      {isLoggedIn && (
        <div className="navbar-profile-wrapper ms-1 ms-md-2" ref={dropdownRef}>
          <button
            type="button"
            className={`navbar-profile-trigger ${isDropdownOpen ? "active" : ""}`}
            onClick={() => setIsDropdownOpen((prev) => !prev)}
            aria-expanded={isDropdownOpen}
            aria-haspopup="true"
            aria-label="User profile menu"
          >
            <div className="navbar-profile-avatar-ring">
              <img
                src={avatarSrc}
                alt={displayName}
                className="navbar-profile-avatar-img"
                onError={() => setImageFailed(true)}
              />
            </div>
          </button>

          {isDropdownOpen && (
            <div className="navbar-profile-dropdown" role="menu">
              {/* Header Profile Summary */}
              <div
                className="navbar-profile-dropdown-header"
                onClick={() => handleNavigate("/user-profile")}
                title="View Profile"
              >
                <img
                  src={avatarSrc}
                  alt={displayName}
                  className="navbar-profile-header-avatar"
                  onError={() => setImageFailed(true)}
                />
                <div className="navbar-profile-header-info">
                  <span className="navbar-profile-header-name">
                    {displayName}
                  </span>
                  {displayEmail && (
                    <span className="navbar-profile-header-email">
                      {displayEmail}
                    </span>
                  )}
                </div>
              </div>

              <div className="navbar-profile-dropdown-divider" />

              {/* Navigation Links */}
              <ul className="navbar-profile-menu-list">
                <li>
                  <button
                    type="button"
                    className="navbar-profile-menu-item"
                    onClick={() => handleNavigate("/user-profile")}
                  >
                    <PersonOutlineComp className="navbar-profile-menu-icon" />
                    <span className="navbar-profile-menu-label">
                      My Profile
                    </span>
                  </button>
                </li>
                <li>
                  <button
                    type="button"
                    className="navbar-profile-menu-item"
                    onClick={() => handleNavigate("/BuyingSide")}
                  >
                    <StorefrontComp className="navbar-profile-menu-icon" />
                    <span className="navbar-profile-menu-label">
                      Business Showcase
                    </span>
                  </button>
                </li>
                <li>
                  <button
                    type="button"
                    className="navbar-profile-menu-item"
                    onClick={() => handleNavigate("/SellingSide")}
                  >
                    <BusinessComp className="navbar-profile-menu-icon" />
                    <span className="navbar-profile-menu-label">
                      Business Creation
                    </span>
                  </button>
                </li>
                <li>
                  <button
                    type="button"
                    className="navbar-profile-menu-item"
                    onClick={() => handleNavigate("/profile-setting")}
                  >
                    <SettingsComp className="navbar-profile-menu-icon" />
                    <span className="navbar-profile-menu-label">Settings</span>
                  </button>
                </li>

                <div className="navbar-profile-dropdown-divider" />

                <li>
                  <button
                    type="button"
                    className="navbar-profile-menu-item navbar-profile-logout-btn"
                    onClick={handleLogoutClick}
                  >
                    <LogoutComp className="navbar-profile-menu-icon" />
                    <span className="navbar-profile-menu-label">Logout</span>
                  </button>
                </li>
              </ul>
            </div>
          )}
        </div>
      )}
      <NavbarAIChat
        isOpen={isAIChatOpen}
        isMinimized={isAIChatMinimized}
        userName={currentUser?.fullname || currentUser?.name}
        onClose={() => {
          setIsAIChatOpen(false);
          setIsAIChatMinimized(false);
        }}
        onMinimize={() => setIsAIChatMinimized(true)}
        onRestore={() => setIsAIChatMinimized(false)}
      />
    </NavComp>
  );
};

export { NavbarActions };
export default NavbarActions;
